import { log } from '../utils/logger';

/**
 * Users API module
 * Handles all user-related API operations
 */

import { db, supabase } from '../lib/supabase';
import { createAPIMethod } from './base';

/**
 * User API namespace
 */
export const userAPI = {
  /**
   * Get all users (TeamRezy employees only)
   * @returns {Promise<APIResponse>}
   */
  getAll: createAPIMethod(
    async () => {
      // Use the SECURITY DEFINER function to get all users
      const { data: allUsersData, error: usersError } = await db.getAllUsers();
      
      if (usersError) throw usersError;
      
      // Filter to only active users and sort by created_at
      const usersData = (allUsersData || [])
        .filter(user => user.is_active)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      return usersData;
    },
    { operation: 'Get all users' }
  ),

  /**
   * Get all users with team associations
   * @returns {Promise<APIResponse>}
   */
  getAllWithTeams: createAPIMethod(
    async () => {
      // First get all users
      const { data: usersData, error: usersError } = await db.getAllUsers();
      
      if (usersError) throw usersError;
      
      // Filter to only active users
      const activeUsers = (usersData || [])
        .filter(user => user.is_active)
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      
      // Fetch team associations more carefully to avoid hitting limits
      // Process users individually if needed to handle users with many teams
      let allUserAssociations = [];
      const MAX_ASSOCIATIONS_PER_USER = 20; // Reasonable limit for display
      
      // First try to get all associations in one query for efficiency
      const { data: allAssociations, error: bulkError } = await supabase
        .from('user_team_roles')
        .select(`
          user_id,
          role,
          teams:team_id!inner (
            id,
            name,
            is_active
          )
        `)
        .in('user_id', activeUsers.map(u => u.id))
        .eq('teams.is_active', true)
        .limit(1000);
      
      if (!bulkError && allAssociations) {
        allUserAssociations = allAssociations;
      } else if (bulkError && bulkError.message?.includes('limit')) {
        // If we hit the limit, fall back to individual user queries
        log.warn('Hit query limit, fetching team associations per user');
        
        for (const user of activeUsers) {
          const { data: userAssocs, error: userError } = await supabase
            .from('user_team_roles')
            .select(`
              user_id,
              role,
              teams:team_id!inner (
                id,
                name,
                is_active
              )
            `)
            .eq('user_id', user.id)
            .eq('teams.is_active', true)
            .limit(MAX_ASSOCIATIONS_PER_USER); // Limit per user to prevent display issues
          
          if (!userError && userAssocs) {
            allUserAssociations.push(...userAssocs);
            if (userAssocs.length === MAX_ASSOCIATIONS_PER_USER) {
              log.warn(`User ${user.email} has ${MAX_ASSOCIATIONS_PER_USER}+ team associations (showing first ${MAX_ASSOCIATIONS_PER_USER})`);
            }
          }
        }
      } else if (bulkError) {
        log.error('Error fetching team associations:', bulkError);
      }
      
      
      // Map team associations to users
      const usersWithTeams = activeUsers.map(user => {
        const userAssociations = allUserAssociations.filter(
          assoc => assoc.user_id === user.id
        );
        
        const teamAssociations = userAssociations.map(assoc => ({
          team_name: assoc.teams?.name || 'Unknown Team',
          role: assoc.role,
          is_primary: assoc.role === 'primary_manager'
        }));
        
        
        return {
          ...user,
          teamAssociations
        };
      });
      
      return usersWithTeams;
    },
    { 
      operation: 'Get all users with teams',
      transformData: (users) => {
        log.info(`📝 Loaded ${users.length} users with team associations`);
        return users;
      }
    }
  ),

  /**
   * Get a single user by ID
   * @param {string} id - User ID
   * @returns {Promise<APIResponse>}
   */
  getById: createAPIMethod(
    async (id) => {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    },
    { operation: 'Get user by ID' }
  ),

  /**
   * Get user with team associations
   * @param {string} userId - User ID
   * @returns {Promise<APIResponse>}
   */
  getUserWithTeams: createAPIMethod(
    async (userId) => {
      // Get user data
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (userError) throw userError;
      
      // Get user's team associations
      const { data: associations, error: assocError } = await supabase
        .from('user_team_roles')
        .select(`
          role,
          teams:team_id (
            id,
            name
          )
        `)
        .eq('user_id', userId)
        .eq('teams.is_active', true);
      
      if (assocError) throw assocError;
      
      const teamAssociations = (associations || []).map(assoc => ({
        team_name: assoc.teams?.name || 'Unknown Team',
        role: assoc.role,
        is_primary: assoc.role === 'primary_manager'
      }));
      
      return {
        ...userData,
        teamAssociations
      };
    },
    { operation: 'Get user with teams' }
  ),

  /**
   * Create a new user
   * @param {Object} userData - User data
   * @returns {Promise<APIResponse>}
   */
  create: createAPIMethod(
    async (userData) => {
      const response = await db.createUser(userData);
      
      if (response.error) throw response.error;
      return response.data;
    },
    { 
      operation: 'Create user',
      transformData: (user) => {
        log.info('📝 New user created:', user.email);
        return user;
      }
    }
  ),

  /**
   * Update an existing user
   * @param {string} id - User ID
   * @param {Object} updates - Updated user data
   * @returns {Promise<APIResponse>}
   */
  update: createAPIMethod(
    async (id, updates) => {
      // Update the user without trying to select (RLS may prevent select)
      const { error: updateError } = await supabase
        .from('users')
        .update(updates)
        .eq('id', id);
      
      if (updateError) throw updateError;
      
      // Since we can't reliably fetch the updated user due to RLS,
      // return the updates merged with the ID
      // The component should handle merging with existing user data
      return {
        id,
        ...updates,
        updated_at: new Date().toISOString()
      };
    },
    { operation: 'Update user' }
  ),

  /**
   * Update user password (via Edge Function)
   * @param {string} targetUserId - ID of user whose password to update
   * @param {string} newPassword - New password
   * @returns {Promise<APIResponse>}
   */
  updatePassword: createAPIMethod(
    async (targetUserId, newPassword) => {
      const response = await supabase.functions.invoke('update-user-password', {
        body: {
          target_user_id: targetUserId,
          new_password: newPassword
        }
      });
      
      if (response.error) {
        throw new Error(response.error.message || 'Password update failed');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'Password update failed');
      }
      
      // Email confirmation is now handled by the Edge Function
      return response.data;
    },
    { operation: 'Update user password' }
  ),

  /**
   * Deactivate a user (soft delete)
   * @param {string} id - User ID
   * @returns {Promise<APIResponse>}
   */
  deactivate: createAPIMethod(
    async (id) => {
      const { data, error } = await supabase
        .from('users')
        .update({ is_active: false })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    { operation: 'Deactivate user' }
  ),

  /**
   * Reactivate a user
   * @param {string} id - User ID
   * @returns {Promise<APIResponse>}
   */
  reactivate: createAPIMethod(
    async (id) => {
      const { data, error } = await supabase
        .from('users')
        .update({ is_active: true })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    { operation: 'Reactivate user' }
  ),

  /**
   * Get users by role
   * @param {string} role - User role
   * @returns {Promise<APIResponse>}
   */
  getByRole: createAPIMethod(
    async (role) => {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('role', role)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    { operation: `Get users by role` }
  ),

  /**
   * Get users for a specific team
   * @param {string} teamId - Team ID
   * @returns {Promise<APIResponse>}
   */
  getByTeam: createAPIMethod(
    async (teamId) => {
      const { data, error } = await supabase
        .from('user_team_roles')
        .select(`
          role,
          users:user_id (
            id,
            email,
            first_name,
            last_name,
            role,
            is_active
          )
        `)
        .eq('team_id', teamId)
        .eq('users.is_active', true);
      
      if (error) throw error;
      
      return data.map(item => ({
        ...item.users,
        team_role: item.role
      }));
    },
    { operation: 'Get users by team' }
  ),

  /**
   * Update user team associations
   * @param {string} userId - User ID
   * @param {Array} teamAssociations - Array of {team_id, role}
   * @returns {Promise<APIResponse>}
   */
  updateTeamAssociations: createAPIMethod(
    async (userId, teamAssociations) => {
      // First, remove existing associations
      const { error: deleteError } = await supabase
        .from('user_team_roles')
        .delete()
        .eq('user_id', userId);
      
      if (deleteError) throw deleteError;
      
      // Then add new associations
      if (teamAssociations && teamAssociations.length > 0) {
        const associations = teamAssociations.map(assoc => ({
          user_id: userId,
          team_id: assoc.team_id,
          role: assoc.role
        }));
        
        const { data, error: insertError } = await supabase
          .from('user_team_roles')
          .insert(associations)
          .select();
        
        if (insertError) throw insertError;
        return data;
      }
      
      return [];
    },
    { operation: 'Update user team associations' }
  ),

  /**
   * Search users by name or email
   * @param {string} searchTerm - Search term
   * @returns {Promise<APIResponse>}
   */
  search: createAPIMethod(
    async (searchTerm) => {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('is_active', true)
        .or(`email.ilike.%${searchTerm}%,first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%`)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      return data;
    },
    { operation: 'Search users' }
  ),

  /**
   * Batch update multiple users
   * @param {Array} updates - Array of {id, data} objects
   * @returns {Promise<APIResponse>}
   */
  batchUpdate: createAPIMethod(
    async (updates) => {
      const results = await Promise.allSettled(
        updates.map(({ id, data }) => 
          supabase
            .from('users')
            .update(data)
            .eq('id', id)
            .select()
            .single()
        )
      );

      return {
        successful: results.filter(r => r.status === 'fulfilled').map(r => r.value.data),
        failed: results.filter(r => r.status === 'rejected').map((r, i) => ({
          id: updates[i].id,
          error: r.reason
        }))
      };
    },
    { operation: 'Batch update users' }
  )
};